import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isCancel } from 'axios'
import { fetchShipmentLabels, listShipments } from '@/api/shipments'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { FilterChips } from '@/components/FilterChips'
import { PaginationControls } from '@/components/PaginationControls'
import { PeriodMenu } from '@/components/PeriodMenu'
import { SortMenu } from '@/components/SortMenu'
import { ShipmentFilterMenu } from '@/components/shipment/ShipmentFilterMenu'
import { ShipmentTableRow } from '@/components/shipment/ShipmentTableRow'
import { AFTER_PARAM, BEFORE_PARAM, clearCursor, parseCursor, writeCursor } from '@/lib/pagination'
import { hasShipmentLabel } from '@/lib/shipment'
import { listShipmentFacetChips, parseShipmentFacetFilters, writeShipmentFacetFilters } from '@/lib/shipmentFilters'
import { DEFAULT_SORT, LIST_SORT_OPTIONS, parseSort, writeSort } from '@/lib/sort'
import type { ShipmentFacetFilters } from '@/lib/shipmentFilters'
import type { DateRange, FilterChip } from '@/lib/filters'
import type { ListSort, ShipmentWithCustomer } from '@/api/types'

const PAGE_SIZE = 200
const SEARCH_PARAM = 'search'
const PRINTING_LABEL = 'Hämtar fraktsedlar...'
const PRINT_ERROR_FALLBACK = 'Kunde inte hämta fraktsedlarna just nu.'

// Tom söktext utelämnas så att URL:en bara innehåller det som faktiskt filtrerar, och ingen markör tas med –
// en ny sökning börjar alltid på första sidan.
function buildSearchParams(searchText: string, facets: ShipmentFacetFilters, sort: ListSort): URLSearchParams {
  const params = new URLSearchParams()
  const search = searchText.trim()

  if (search !== '') {
    params.set(SEARCH_PARAM, search)
  }

  writeShipmentFacetFilters(params, facets)
  writeSort(params, sort)

  return params
}

function formatShipmentCount(count: number): string {
  const noun = count === 1 ? 'leverans' : 'leveranser'

  return `${count} ${noun}`
}

interface SelectionToolbarProps {
  selectedCount: number
  printableCount: number
  totalCount: number
  isPrinting: boolean
  canPrint: boolean
  onClearSelection: () => void
  onPrint: () => void
}

function SelectionToolbar({
  selectedCount,
  printableCount,
  totalCount,
  isPrinting,
  canPrint,
  onClearSelection,
  onPrint
}: SelectionToolbarProps): React.ReactElement {
  const hasSelection = selectedCount > 0
  const summary = hasSelection ? `${selectedCount} valda av ${printableCount} med fraktsedel` : formatShipmentCount(totalCount)
  const printLabel = isPrinting ? PRINTING_LABEL : `Skriv ut fraktsedel (${selectedCount})`

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 px-6 py-4">
      <p className="text-sm text-muted-foreground">{summary}</p>
      <div className="flex items-center gap-2">
        {hasSelection && (
          <Button
            type="button"
            variant="ghost"
            className="rounded-none"
            onClick={onClearSelection}
          >
            Avmarkera
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          className="rounded-none"
          disabled={!canPrint}
          onClick={onPrint}
        >
          {printLabel}
        </Button>
      </div>
    </div>
  )
}

export function DeliveriesPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchText, setSearchText] = useState('')
  const [shipments, setShipments] = useState<ShipmentWithCustomer[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrevious, setHasPrevious] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isPrinting, setIsPrinting] = useState(false)
  const [printError, setPrintError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const searchParam = searchParams.get(SEARCH_PARAM) ?? ''
  const after = parseCursor(searchParams.get(AFTER_PARAM))
  const before = parseCursor(searchParams.get(BEFORE_PARAM))
  // useSearchParams memoiserar på location.search, så facetterna byter identitet bara när URL:en gör det.
  const facetFilters = useMemo(() => parseShipmentFacetFilters(searchParams), [searchParams])
  const sort = parseSort(searchParams, LIST_SORT_OPTIONS, DEFAULT_SORT)

  const firstShipmentId = shipments.at(0)?.id
  const lastShipmentId = shipments.at(-1)?.id

  const facetChipSpecs = listShipmentFacetChips(facetFilters)
  const hasFacetChips = facetChipSpecs.length > 0
  const hasSearch = searchParam !== ''
  const hasActiveFilters = hasSearch || hasFacetChips
  const hasError = errorMessage !== null
  const hasPrintError = printError !== null
  const hasNoShipments = !isLoading && !hasError && shipments.length === 0
  const hasNextPage = !isLoading && hasNext && lastShipmentId !== undefined
  const hasPreviousPage = !isLoading && hasPrevious && firstShipmentId !== undefined
  const showPagination = !hasError && (shipments.length > 0 || after !== undefined || before !== undefined)
  const emptyMessage = hasActiveFilters
    ? 'Inga leveranser hittades.'
    : 'Inga leveranser ännu – aktivera leveranser på en kund så skapas de här.'

  // Valet filtreras alltid genom raderna på skärmen, så ett id från ett tidigare resultat eller en annan sida är verkningslöst.
  const printableShipments = shipments.filter(hasShipmentLabel)
  const selectedShipmentIds = shipments.filter((shipment) => selectedIds.has(shipment.id)).map((shipment) => shipment.id)
  const selectedCount = selectedShipmentIds.length
  const hasSelection = selectedCount > 0
  const hasPrintableShipments = printableShipments.length > 0
  const isAllPrintableSelected = hasPrintableShipments && printableShipments.every((shipment) => selectedIds.has(shipment.id))
  // Utskrift kräver ett aktivt val – annars räcker ett felklick för att skicka 200 etiketter till skrivaren.
  const canPrint = hasSelection && !isPrinting

  // Sökfältet följer URL:en (bakåt/framåt i webbläsaren), men inte markören – annars raderas halvskriven text vid sidbyte.
  useEffect(() => {
    setSearchText(searchParam)
  }, [searchParam])

  useEffect(() => {
    const controller = new AbortController()

    const loadShipments = async (): Promise<void> => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        const result = await listShipments(
          {
            search: searchParam || undefined,
            status: facetFilters.statuses,
            from: facetFilters.period.from || undefined,
            to: facetFilters.period.to || undefined,
            sort,
            limit: PAGE_SIZE,
            after,
            before
          },
          controller.signal
        )

        setShipments(result.shipments)
        setTotalCount(result.total)
        setHasNext(result.has_next)
        setHasPrevious(result.has_previous)
        // Nytt resultat – tidigare val hör inte längre ihop med det som visas.
        setSelectedIds(new Set())
        setPrintError(null)
      } catch (error) {
        // En nyare sökning har redan tagit över – det här svaret ska varken visas eller felmarkeras.
        if (isCancel(error)) return

        setErrorMessage('Kunde inte hämta leveranser just nu.')
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    void loadShipments()

    return () => controller.abort()
  }, [searchParam, facetFilters, sort, after, before])

  const handleSearch = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    setSearchParams(buildSearchParams(searchText, facetFilters, sort))
  }

  // Nollställer även markören i URL:en – annars ligger pagineringen kvar på en sida som inte finns i den ofiltrerade listan.
  const handleReset = (): void => {
    setSearchText('')
    setSelectedIds(new Set())
    setPrintError(null)
    setSearchParams({})
  }

  // Facetter och period slår igenom direkt, till skillnad från sökfältet som väntar på Sök. Markören nollställs så listan börjar om på sida ett.
  const handleFacetsChange = (next: ShipmentFacetFilters): void => {
    setSearchParams((previous) => {
      clearCursor(previous)
      writeShipmentFacetFilters(previous, next)

      return previous
    })
  }

  const handlePeriodChange = (period: DateRange): void => {
    handleFacetsChange({ ...facetFilters, period })
  }

  const handleSortChange = (next: ListSort): void => {
    setSearchParams((previous) => {
      clearCursor(previous)
      writeSort(previous, next)

      return previous
    })
  }

  const navigateToCursor = (cursorParam: string, shipmentId: number): void => {
    setSearchParams((previous) => {
      writeCursor(previous, cursorParam, shipmentId)

      return previous
    })
  }

  // "Nästa" bläddrar vidare i vald sortering och "Föregående" tillbaka mot listans början.
  const handlePreviousPage = (): void => {
    if (firstShipmentId === undefined) {
      return
    }

    navigateToCursor(BEFORE_PARAM, firstShipmentId)
  }

  const handleNextPage = (): void => {
    if (lastShipmentId === undefined) {
      return
    }

    navigateToCursor(AFTER_PARAM, lastShipmentId)
  }

  const handleToggleShipment = (shipmentId: number): void => {
    setSelectedIds((current) => {
      const next = new Set(current)

      if (next.has(shipmentId)) {
        next.delete(shipmentId)
      } else {
        next.add(shipmentId)
      }

      return next
    })
  }

  const handleToggleAllPrintable = (): void => {
    if (isAllPrintableSelected) {
      setSelectedIds(new Set())
      return
    }

    setSelectedIds(new Set(printableShipments.map((shipment) => shipment.id)))
  }

  const handleClearSelection = (): void => {
    setSelectedIds(new Set())
  }

  // Fliken öppnas före await:en – annars hinner webbläsaren tappa klicket och blockera popupen.
  const handlePrintLabels = async (): Promise<void> => {
    const tab = window.open('', '_blank')

    setIsPrinting(true)
    setPrintError(null)

    try {
      const blob = await fetchShipmentLabels({ ids: selectedShipmentIds.join(',') })
      const url = URL.createObjectURL(blob)

      if (tab) {
        tab.location.href = url
      } else {
        window.location.href = url
      }
    } catch (error) {
      tab?.close()
      setPrintError(error instanceof Error ? error.message : PRINT_ERROR_FALLBACK)
    } finally {
      setIsPrinting(false)
    }
  }

  const filterChips: FilterChip[] = facetChipSpecs.map((chip) => ({
    key: chip.key,
    label: chip.label,
    onRemove: () => handleFacetsChange(chip.withoutChip)
  }))

  return (
    <div className="space-y-6">
      <Card className="shadow-none border-border/80">
        <CardHeader>
          <CardTitle className="text-3xl font-serif text-primary">Leveranslista</CardTitle>
          <CardDescription>Tabell med sökning och filtrering.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSearch}
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto_auto]"
          >
            <Input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Sök på kund, kundnummer, leveransnummer, personnummer, telefon eller spårningsnummer"
            />
            <ShipmentFilterMenu
              filters={facetFilters}
              onChange={handleFacetsChange}
            />
            <PeriodMenu
              range={facetFilters.period}
              onChange={handlePeriodChange}
            />
            <SortMenu
              value={sort}
              options={LIST_SORT_OPTIONS}
              onChange={handleSortChange}
            />
            <Button type="submit">Sök</Button>
            {hasActiveFilters && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleReset}
              >
                Rensa
              </Button>
            )}
          </form>

          {hasFacetChips && (
            <div className="mt-3">
              <FilterChips chips={filterChips} />
            </div>
          )}
        </CardContent>
      </Card>

      {hasError && (
        <Card className="shadow-none border-destructive/40">
          <CardContent className="pt-6 text-destructive">{errorMessage}</CardContent>
        </Card>
      )}

      {hasPrintError && (
        <Card className="shadow-none border-destructive/40">
          <CardContent className="pt-6 text-destructive">{printError}</CardContent>
        </Card>
      )}

      {!hasError && (
        <Card className="shadow-none border-border/80">
          <CardContent className="p-0">
            <SelectionToolbar
              selectedCount={selectedCount}
              printableCount={printableShipments.length}
              totalCount={totalCount}
              isPrinting={isPrinting}
              canPrint={canPrint}
              onClearSelection={handleClearSelection}
              onPrint={() => void handlePrintLabels()}
            />
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/50 text-left text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3 font-medium w-10">
                      <input
                        type="checkbox"
                        aria-label="Markera alla med fraktsedel"
                        className="size-4 align-middle accent-primary"
                        checked={isAllPrintableSelected}
                        disabled={!hasPrintableShipments}
                        onChange={handleToggleAllPrintable}
                      />
                    </th>
                    <th className="px-6 py-3 font-medium">Kund</th>
                    <th className="px-6 py-3 font-medium">Leverans</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium">Spårningsnummer</th>
                    <th className="px-6 py-3 font-medium">Skapad</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((shipment) => (
                    <ShipmentTableRow
                      key={shipment.id}
                      shipment={shipment}
                      isSelected={selectedIds.has(shipment.id)}
                      onToggleSelected={handleToggleShipment}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {isLoading && <p className="px-6 py-8 text-sm text-muted-foreground">Laddar leveranser...</p>}
            {hasNoShipments && <p className="px-6 py-8 text-sm text-muted-foreground">{emptyMessage}</p>}

            {showPagination && (
              <PaginationControls
                hasPreviousPage={hasPreviousPage}
                hasNextPage={hasNextPage}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
